import { useForm } from 'react-hook-form';
import { Mail, MessageSquare, Clock, AlertCircle } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useToast } from '../providers/ToastProvider.tsx';
import { getSendContactEmailUrl } from '../utils/index.ts';
import { LayoutWrapper } from '../components/LayoutWrapper.tsx';
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from 'react-google-recaptcha-v3';

const env = import.meta.env.VITE_ENV || 'dev';
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

function ContactForm() {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  const { formatMessage } = useIntl();
  const { showToast } = useToast();

  const onSubmit = async (data: ContactFormData) => {
    try {
      if (!executeRecaptcha) {
        throw new Error('reCAPTCHA not available');
      }

      // Execute reCAPTCHA and get token
      const recaptchaToken = await executeRecaptcha('contact_form');

      const response = await fetch(getSendContactEmailUrl(env), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...data, recaptchaToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      reset();
      showToast(
        formatMessage({
          id: 'contact.messageSent',
          defaultMessage:
            'Сообщение отправлено! Мы свяжемся с вами в ближайшее время.',
        }),
        'success',
      );
    } catch (error) {
      console.error('Error:', error);

      showToast(
        formatMessage({
          id: 'contact.messageError',
          defaultMessage:
            'Произошла ошибка при отправке сообщения. Пожалуйста, попробуйте позже.',
        }),
        'error',
      );
    }
  };

  const noSpecialCharsPattern = /^[a-zA-Zа-яА-ЯёЁ0-9\s.,-]*$/;

  return (
    <>
      {/* Hero Section */}
      <div className='bg-blue-600 text-white py-16'>
        <div className='container mx-auto px-4 text-center'>
          <MessageSquare className='w-16 h-16 mx-auto mb-6' />
          <h1 className='text-4xl font-bold mb-4'>
            {formatMessage({
              id: 'contact.heroTitle',
              defaultMessage: 'Свяжитесь с нами',
            })}
          </h1>
          <p className='text-xl text-blue-100 max-w-2xl mx-auto'>
            {formatMessage({
              id: 'contact.heroSubtitle',
              defaultMessage: 'У вас есть вопросы? Мы всегда готовы помочь!',
            })}
          </p>
        </div>
      </div>

      <div className='container mx-auto px-4 py-12'>
        <div className='max-w-5xl mx-auto'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-8'>
            {/* Support Information */}
            <div className='md:col-span-1 space-y-6'>
              <div className='bg-white rounded-xl shadow-sm p-6'>
                <h2 className='text-xl font-semibold mb-6'>
                  {formatMessage({
                    id: 'contact.contactInfo',
                    defaultMessage: 'Контактная информация',
                  })}
                </h2>
                <div className='space-y-4'>
                  <div className='flex items-start space-x-3'>
                    <Mail className='w-5 h-5 text-blue-600 mt-1' />
                    <div>
                      <p className='font-medium'>
                        {formatMessage({
                          id: 'contact.email',
                          defaultMessage: 'Email',
                        })}
                      </p>
                      <a
                        href='mailto:help@novoservice.net'
                        className='text-blue-600 hover:text-blue-700'
                      >
                        help@novoservice.net
                      </a>
                    </div>
                  </div>

                  <div className='flex items-start space-x-3'>
                    <MessageSquare className='w-5 h-5 text-blue-600 mt-1' />
                    <div>
                      <p className='font-medium'>
                        {formatMessage({
                          id: 'contact.telegram',
                          defaultMessage: 'Telegram',
                        })}
                      </p>
                      <a
                        href='https://telegram.me/novoservice_net'
                        className='text-blue-600 hover:text-blue-700'
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        @novoservice_net
                      </a>
                    </div>
                  </div>

                  <div className='flex items-start space-x-3'>
                    <Clock className='w-5 h-5 text-blue-600 mt-1' />
                    <div>
                      <p className='font-medium'>
                        {formatMessage({
                          id: 'contact.workingHours',
                          defaultMessage: 'Время работы',
                        })}
                      </p>
                      <p className='text-gray-600'>Пн-Пт: 9:00 - 18:00</p>
                      <p className='text-gray-600'>Сб-Вс: 10:00 - 16:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className='bg-blue-50 rounded-xl p-6'>
                <h3 className='font-semibold mb-2'>
                  {formatMessage({
                    id: 'contact.quickReply',
                    defaultMessage: 'Быстрый ответ',
                  })}
                </h3>
                <p className='text-sm text-gray-600'>
                  {formatMessage({
                    id: 'contact.quickReplyHint',
                    defaultMessage:
                      'Мы стараемся отвечать на все запросы в течение 24 часов.',
                  })}
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className='md:col-span-2'>
              <div className='bg-white rounded-xl shadow-sm p-6'>
                <h2 className='text-xl font-semibold mb-6'>
                  {formatMessage({
                    id: 'contact.sendMessage',
                    defaultMessage: 'Отправить сообщение',
                  })}
                </h2>
                <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
                  {/* Name Field */}
                  <div>
                    <label
                      htmlFor='name'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      {formatMessage({
                        id: 'contact.nameLabel',
                        defaultMessage: 'Ваше имя',
                      })}
                    </label>
                    <input
                      type='text'
                      id='name'
                      {...register('name', {
                        required: formatMessage({
                          id: 'contact.nameRequired',
                          defaultMessage: 'Пожалуйста, введите ваше имя',
                        }),
                        minLength: {
                          value: 2,
                          message: formatMessage({
                            id: 'contact.nameMinLength',
                            defaultMessage:
                              'Имя должно содержать минимум 2 символа',
                          }),
                        },
                        maxLength: {
                          value: 50,
                          message: formatMessage({
                            id: 'contact.nameMaxLength',
                            defaultMessage:
                              'Имя не может превышать 50 символов',
                          }),
                        },
                        pattern: {
                          value: noSpecialCharsPattern,
                          message: formatMessage({
                            id: 'contact.nameNoSpecialChars',
                            defaultMessage:
                              'Имя не может содержать специальные символы',
                          }),
                        },
                      })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.name && (
                      <p className='mt-1 text-sm text-red-600 flex items-center'>
                        <AlertCircle className='w-4 h-4 mr-1' />
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  {/* Email Field */}
                  <div>
                    <label
                      htmlFor='email'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      {formatMessage({
                        id: 'contact.emailLabel',
                        defaultMessage: 'Email',
                      })}
                    </label>
                    <input
                      type='email'
                      id='email'
                      {...register('email', {
                        required: formatMessage({
                          id: 'contact.emailRequired',
                          defaultMessage: 'Пожалуйста, введите ваш email',
                        }),
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: formatMessage({
                            id: 'contact.emailPattern',
                            defaultMessage: 'Неверный формат email',
                          }),
                        },
                        maxLength: {
                          value: 100,
                          message: formatMessage({
                            id: 'contact.emailMaxLength',
                            defaultMessage:
                              'Email не может превышать 100 символов',
                          }),
                        },
                      })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.email ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.email && (
                      <p className='mt-1 text-sm text-red-600 flex items-center'>
                        <AlertCircle className='w-4 h-4 mr-1' />
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  {/* Subject Field */}
                  <div>
                    <label
                      htmlFor='subject'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      {formatMessage({
                        id: 'contact.subjectLabel',
                        defaultMessage: 'Тема',
                      })}
                    </label>
                    <input
                      type='text'
                      id='subject'
                      {...register('subject', {
                        required: formatMessage({
                          id: 'contact.subjectRequired',
                          defaultMessage: 'Пожалуйста, укажите тему сообщения',
                        }),
                        minLength: {
                          value: 5,
                          message: formatMessage({
                            id: 'contact.subjectMinLength',
                            defaultMessage:
                              'Тема должна содержать минимум 5 символов',
                          }),
                        },
                        maxLength: {
                          value: 200,
                          message: formatMessage({
                            id: 'contact.subjectMaxLength',
                            defaultMessage:
                              'Тема не может превышать 200 символов',
                          }),
                        },
                        pattern: {
                          value: noSpecialCharsPattern,
                          message: formatMessage({
                            id: 'contact.subjectNoSpecialChars',
                            defaultMessage:
                              'Тема не может содержать специальные символы',
                          }),
                        },
                      })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.subject ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.subject && (
                      <p className='mt-1 text-sm text-red-600 flex items-center'>
                        <AlertCircle className='w-4 h-4 mr-1' />
                        {errors.subject.message}
                      </p>
                    )}
                  </div>

                  {/* Message Field */}
                  <div>
                    <label
                      htmlFor='message'
                      className='block text-sm font-medium text-gray-700 mb-1'
                    >
                      {formatMessage({
                        id: 'contact.messageLabel',
                        defaultMessage: 'Сообщение',
                      })}
                    </label>
                    <textarea
                      id='message'
                      rows={5}
                      {...register('message', {
                        required: formatMessage({
                          id: 'contact.messageRequired',
                          defaultMessage: 'Пожалуйста, введите ваше сообщение',
                        }),
                        minLength: {
                          value: 10,
                          message: formatMessage({
                            id: 'contact.messageMinLength',
                            defaultMessage:
                              'Сообщение должно содержать минимум 10 символов',
                          }),
                        },
                        maxLength: {
                          value: 5000,
                          message: formatMessage({
                            id: 'contact.messageMaxLength',
                            defaultMessage:
                              'Сообщение не может превышать 5000 символов',
                          }),
                        },
                        pattern: {
                          value: noSpecialCharsPattern,
                          message: formatMessage({
                            id: 'contact.messageNoSpecialChars',
                            defaultMessage:
                              'Сообщение не может содержать специальные символы',
                          }),
                        },
                      })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.message ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.message && (
                      <p className='mt-1 text-sm text-red-600 flex items-center'>
                        <AlertCircle className='w-4 h-4 mr-1' />
                        {errors.message.message}
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div>
                    <button
                      type='submit'
                      disabled={isSubmitting}
                      className={`w-full bg-blue-600 text-white px-6 py-3 rounded-lg ${
                        isSubmitting
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-blue-700'
                      }`}
                    >
                      {isSubmitting
                        ? formatMessage({
                            id: 'contact.submitting',
                            defaultMessage: 'Отправка...',
                          })
                        : formatMessage({
                            id: 'contact.submit',
                            defaultMessage: 'Отправить сообщение',
                          })}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function Contact() {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_SITE_KEY}
      scriptProps={{
        async: true,
        defer: true,
        appendTo: 'head',
      }}
    >
      <LayoutWrapper hasHero>
        <ContactForm />
      </LayoutWrapper>
    </GoogleReCaptchaProvider>
  );
}
